
export enum AppState {
  INITIALIZING = 'INITIALIZING',
  UPLOAD = 'UPLOAD',
  EDITOR = 'EDITOR',
  PROCESSING = 'PROCESSING',
  RESULT = 'RESULT',
  ERROR = 'ERROR'
}

/** One furniture/decor piece extracted from a staged hero for cross-angle consistency. */
export interface FurnitureInventoryPiece {
  id: string;
  name: string;
  colour: string;
  material: string;
  /** Position relative to walls / windows / doors / other pieces. */
  position: string;
}

/** Structured furniture layout from the staged hero, used to place the same set into follow-up angles. */
export interface FurnitureInventory {
  styleNotes: string;
  pieces: FurnitureInventoryPiece[];
}

export interface ImageState {
  originalFile: File | null;
  originalUrl: string | null;
  processedUrl: string | null;
  maskDataUrl: string | null; // For debugging or history if needed
  /** True when the source image is a 360° equirectangular panorama (2:1). */
  is360?: boolean;
}

export interface DeclutterOptions {
  prompt: string;
  brushSize: number;
}

export interface WatermarkSettings {
  imageUrl: string;
  x: number; // Normalized 0-1
  y: number; // Normalized 0-1
  scale: number; // Normalized scale relative to image width
  opacity: number; // 0-1
}

export interface UserProfile {
  id: string;
  email: string;
  credits: number;
  subscription_plan?: string;
  stripe_customer_id?: string;
  referral_code?: string;
}

export interface ReferralReward {
  id: string;
  bonus_credits: number;
  referrer_bonus_credits?: number;
  purchased_credits: number;
  created_at: string;
  referred_email: string;
  referred_name?: string;
  reward_kind?: string;
  status?: string;
}

export interface ReferralStats {
  success: boolean;
  referral_code: string;
  signup_count: number;
  friends_joined?: number;
  total_bonus_credits: number;
  credits_earned?: number;
  invite_base_url?: string;
  rewards: ReferralReward[];
}

export interface GeneratedImage {
  id: string;
  user_id: string;
  input_url: string;
  output_url: string;
  prompt?: string;
  status: string;
  created_at: string;
  type?: string; // Added to match new database schema
  service_type?: 'photoclear' | 'staging'; // Identifies which app created the image
  ai_classification?: 'AI_ASSISTED' | 'AI_MODIFIED' | 'AI_GENERATED' | null;
  ai_operation?: string | null;
  ai_created_at?: string | null;
  source_asset_id?: string | null;
  visible_disclosure_applied?: boolean | null;
}

export interface CreditTransaction {
  id: string;
  user_id: string;
  amount: number;
  type: string;
  description: string;
  created_at: string;
}

export interface Price {
  id: string;
  product_id: string;
  active: boolean;
  currency: string;
  type: 'one_time' | 'recurring';
  unit_amount: number;
  interval?: string;
  interval_count?: number;
}

export interface Product {
  id: string;
  active: boolean;
  name: string;
  description?: string;
  image?: string;
  metadata?: {
    credits?: string; // lowercase
    bonus?: string;
    popular?: string;
    [key: string]: any;
  };
  prices?: Price[];
}

export interface AdminUser {
  user_id: string;
  role: 'admin' | 'superadmin';
  created_at: string;
}

export interface PromoCode {
  id: string;
  code: string;
  credits: number;
  max_uses: number | null;
  current_uses: number;
  expires_at: string | null;
  active: boolean;
  created_by: string | null;
  description: string | null;
  created_at: string;
}

export interface PromoRedemption {
  id: string;
  promo_code_id: string | null;
  code: string;
  user_id: string;
  credits_granted: number;
  redeemed_at: string;
}

export interface AdminAction {
  id: string;
  admin_id: string | null;
  action_type: 'grant_credits' | 'create_promo' | 'update_promo' | 'deactivate_promo';
  target_user_id: string | null;
  details: any;
  created_at: string;
}

export interface RedeemPromoResult {
  success: boolean;
  error?: 'NOT_AUTHENTICATED' | 'INVALID_CODE' | 'CODE_INACTIVE' | 'CODE_EXPIRED' | 'CODE_EXHAUSTED' | 'ALREADY_REDEEMED';
  credits_added?: number;
  code?: string;
}

// =========================================
// BEHAVIOR MONITORING TYPES
// =========================================

export type EventType = 
  | 'magic_link_requested'
  | 'login'
  | 'generation_started'
  | 'generation_completed'
  | 'generation_failed'
  | 'credits_granted'
  | 'credits_used'
  | 'payment_completed'
  | 'promo_redeemed'
  | 'user_status_changed'
  | 'ip_control_changed'
  | 'domain_control_changed'
  | 'staging_animation_opened'
  | 'staging_animation_before_uploaded'
  | 'staging_animation_after_uploaded'
  | 'staging_animation_started'
  | 'staging_animation_completed'
  | 'staging_animation_failed'
  | 'staging_animation_downloaded'
  | 'virtual_staging_animate_clicked';

export type UserRiskStatus = 'ok' | 'watch' | 'restricted' | 'banned';

export type ControlStatus = 'ok' | 'throttled' | 'blocked';

export type DomainControlStatus = 'ok' | 'throttled' | 'require_payment' | 'blocked';

export interface EventLog {
  id: string;
  created_at: string;
  event_type: EventType;
  user_id: string | null;
  user_email: string | null;
  ip_address: string | null;
  user_agent: string | null;
  metadata: Record<string, any>;
}

export interface UserWithStatus {
  id: string;
  email: string;
  credits: number;
  created_at: string;
  risk_status: UserRiskStatus;
  status_reason: string | null;
  status_updated_at: string | null;
  event_count: number;
  last_activity: string | null;
}

export interface UserTimeline {
  user: {
    id: string;
    email: string;
    credits: number;
    created_at: string;
    risk_status: UserRiskStatus;
    status_reason: string | null;
    status_updated_at: string | null;
  };
  events: EventLog[];
}

export interface IPAnalytics {
  ip_address: string;
  event_count: number;
  user_count: number;
  email_count: number;
  emails?: string[];
  last_activity: string;
  first_activity: string;
  control_status: ControlStatus;
  throttle_multiplier: number;
  control_reason: string | null;
}

export interface DomainAnalytics {
  domain: string;
  user_count: number;
  event_count: number;
  last_activity: string;
  control_status: DomainControlStatus;
  throttle_multiplier: number;
  control_reason: string | null;
}

export interface ActivityFeedFilters {
  eventType?: EventType;
  userId?: string;
  ipAddress?: string;
  limit?: number;
  offset?: number;
}

export interface RateLimitCheck {
  allowed: boolean;
  reason?: 'domain_blocked' | 'ip_blocked' | 'rate_limited';
  message?: string;
  require_payment?: boolean;
}

// =========================================
// GENERATIONS & CREDIT LEDGER TYPES
// =========================================

export type GenerationStatus = 'queued' | 'started' | 'completed' | 'failed';

export interface Generation {
  id: string;
  created_at: string;
  user_id: string | null;
  user_email: string | null;
  status: GenerationStatus;
  credits_used: number;
  input_asset_path: string | null;
  output_image_path: string | null;
  ip_address: string | null;
  metadata: Record<string, any>;
  completed_at: string | null;
  error_message: string | null;
}

export type CreditRefType = 'generation' | 'purchase' | 'promo' | 'admin_grant' | 'refund' | 'welcome_bonus';

export interface CreditLedgerEntry {
  id: string;
  created_at: string;
  user_id: string;
  delta: number;
  reason: string;
  ref_type: CreditRefType | null;
  ref_id: string | null;
  created_by: string | null;
  metadata: Record<string, any>;
}

export interface GenerationsFilters {
  userId?: string;
  userEmail?: string;
  status?: GenerationStatus;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export interface UserWithStatusExtended extends UserWithStatus {
  credit_balance: number;
  generation_count: number;
}

// =========================================
// SALES METRICS TYPES
// =========================================

export interface SalesMetrics {
  gross_revenue_usd: number;
  revenue_gbp?: number;
  revenue_usd?: number;
  revenue_eur?: number;
  sales_with_amount?: number;
  sales_without_amount?: number;
  credits_granted?: number;
  credits_sold: number;
  credits_used: number;
  credits_refunded: number;
  total_cost_usd: number;
  gross_margin_usd: number;
  gross_margin_pct: number;
  completed_generations: number;
  failed_generations: number;
  total_generations: number;
  failed_rate_pct: number;
  avg_cost_per_generation: number;
  avg_margin_per_generation: number;
  credit_price_usd: number;
}

export type SalesPeriod = 'today' | '7d' | '30d' | 'all';
